import { useMutation, useQuery } from "@apollo/client/react";
import {
  ArchiveProductDocument,
  CreateProductDocument,
  DeleteProductDocument,
  MyProductDocument,
  MyProductsDocument,
  MyStoreDocument,
  UpdateProductDocument,
} from "@/generated/graphql";
import { graphQLErrorMessage } from "@/lib/graphqlErrors";
import type { Product, ProductInput } from "@/types/models";

export function useProducts() {
  const { data: storeData, loading: storeLoading } = useQuery(MyStoreDocument, {
    fetchPolicy: "network-only",
  });

  const { data, loading, error, refetch } = useQuery(MyProductsDocument, {
    fetchPolicy: "network-only",
    skip: !storeData?.myStore,
  });

  const products: Product[] = data?.myProducts ?? [];
  const store = storeData?.myStore ?? null;

  const [createProductMutation, { loading: creating }] = useMutation(CreateProductDocument, {
    refetchQueries: [{ query: MyProductsDocument }],
  });

  const [updateProductMutation, { loading: updating }] = useMutation(UpdateProductDocument, {
    refetchQueries: [{ query: MyProductsDocument }],
  });

  const [archiveProductMutation, { loading: archiving }] = useMutation(ArchiveProductDocument, {
    refetchQueries: [{ query: MyProductsDocument }],
  });

  const [deleteProductMutation, { loading: deleting }] = useMutation(DeleteProductDocument, {
    refetchQueries: [{ query: MyProductsDocument }],
  });

  const createProduct = (
    input: ProductInput,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    createProductMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível criar o produto."));
      },
    });
  };

  const updateProduct = (
    input: ProductInput,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    updateProductMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível atualizar o produto."));
      },
    });
  };

  const archiveProduct = (
    id: string,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    archiveProductMutation({
      variables: { input: { id } },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível arquivar o produto."));
      },
    });
  };

  const deleteProduct = (
    id: string,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    deleteProductMutation({
      variables: { id },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível excluir o produto."));
      },
    });
  };

  return {
    store,
    products,
    loading: storeLoading || loading,
    error,
    refetch,
    createProduct,
    updateProduct,
    archiveProduct,
    deleteProduct,
    saving: creating || updating,
    archiving,
    deleting,
  };
}

export function useProduct(id: string | undefined) {
  const { data, loading, error } = useQuery(MyProductDocument, {
    variables: { id: id! },
    fetchPolicy: "network-only",
    skip: !id,
  });

  return {
    product: data?.myProduct ?? null,
    loading,
    error,
  };
}
